import React, { useState, useEffect, useRef } from "react";
import searchService, { City } from "../../services/searchService";
import {
  VerticalType,
  TripType,
  VERTICALS,
  VERTICAL_OPTIONS,
  TRIP_TYPES_BY_VERTICAL,
  DEFAULT_TRIP_TYPE_BY_VERTICAL,
  isSourceFieldVisible,
  isDestinationFieldVisible,
  isReturnLegRequired,
} from "../../constants/tripTypes";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import LocationDistanceMap from "../LocationDistanceMap/LocationDistanceMap";
import { DrivingDistanceResult } from "../../utils/distanceHelper";

interface SearchSectionProps {
  activeTab: VerticalType;
  setActiveTab: (tab: VerticalType) => void;
  tripType: TripType;
  setTripType: (tripType: TripType) => void;
  source: City | null;
  setSource: (source: City | null) => void;
  destination: City | null;
  setDestination: (destination: City | null) => void;
  departureDate: string;
  setDepartureDate: (date: string) => void;
  returnDate: string;
  setReturnDate: (date: string) => void;
  pickupTime: string;
  setPickupTime: (time: string) => void;
  dropTime: string;
  setDropTime: (time: string) => void;
  sourceSearch: string;
  setSourceSearch: (search: string) => void;
  destSearch: string;
  setDestSearch: (search: string) => void;
  handleSearch: () => void;
  handleClearFilter: () => void;
  isSearchEnabled: boolean;
  showClearButton: boolean;
  animationEnabled?: boolean;
  onDistanceComputed?: (result: DrivingDistanceResult | null) => void;
}

const SearchSection: React.FC<SearchSectionProps> = ({
  activeTab,
  setActiveTab,
  tripType,
  setTripType,
  source,
  setSource,
  destination,
  setDestination,
  departureDate,
  setDepartureDate,
  returnDate,
  setReturnDate,
  pickupTime,
  setPickupTime,
  dropTime,
  setDropTime,
  sourceSearch,
  setSourceSearch,
  destSearch,
  setDestSearch,
  handleSearch,
  handleClearFilter,
  isSearchEnabled,
  showClearButton,
  animationEnabled = true,
  onDistanceComputed,
}) => {
  // City search states
  const [sourceCities, setSourceCities] = useState<City[]>([]);
  const [destinationCities, setDestinationCities] = useState<City[]>([]);
  const [isLoadingSource, setIsLoadingSource] = useState<boolean>(false);
  const [isLoadingDest, setIsLoadingDest] = useState<boolean>(false);

  // UI states
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const [destDropdownOpen, setDestDropdownOpen] = useState(false);

  // Refs for detecting clicks outside dropdowns
  const sourceRef = useRef<HTMLDivElement>(null);
  const destRef = useRef<HTMLDivElement>(null);
  const departureDateRef = useRef<HTMLInputElement>(null);
  const returnDateRef = useRef<HTMLInputElement>(null);
  const pickupTimeRef = useRef<HTMLInputElement>(null);
  const dropTimeRef = useRef<HTMLInputElement>(null);

  // Load initial cities on component mount
  useEffect(() => {
    const loadInitialCities = async () => {
      try {
        const cities = await searchService.searchCities("", 10);
        setSourceCities(cities);
        setDestinationCities(cities);
      } catch (error) {
        console.error("Failed to load initial cities", error);
      }
    };

    loadInitialCities();
  }, []);

  // Handle outside clicks for dropdowns
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        sourceRef.current &&
        !sourceRef.current.contains(event.target as Node)
      ) {
        setSourceDropdownOpen(false);
      }
      if (destRef.current && !destRef.current.contains(event.target as Node)) {
        setDestDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Reset destination when switching to Airport + Pickup (destination is implicitly the airport)
  useEffect(() => {
    if (!isDestinationFieldVisible(activeTab, tripType) && destination !== null) {
      setDestination(null);
      setDestSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tripType]);

  // Debounced city search
  const debouncedSourceSearch = useRef(
    debounce(async (query: string) => {
      try {
        setIsLoadingSource(true);
        const results = await searchService.searchCities(query);
        setSourceCities(results);
      } catch (error) {
        console.error("Source search error:", error);
      } finally {
        setIsLoadingSource(false);
      }
    }, 300)
  ).current;

  const debouncedDestSearch = useRef(
    debounce(async (query: string) => {
      try {
        setIsLoadingDest(true);
        const results = await searchService.searchCities(query);
        setDestinationCities(results);
      } catch (error) {
        console.error("Destination search error:", error);
      } finally {
        setIsLoadingDest(false);
      }
    }, 300)
  ).current;

  // Handle source search input change
  const handleSourceSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSourceSearch(query);
    setSourceDropdownOpen(true);
    debouncedSourceSearch(query);
  };

  // Handle destination search input change
  const handleDestSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setDestSearch(query);
    setDestDropdownOpen(true);
    debouncedDestSearch(query);
  };

  // Select source city
  const selectSourceCity = (city: City) => {
    setSource(city);
    setSourceSearch(city.name);
    setSourceDropdownOpen(false);
  };

  // Select destination city
  const selectDestinationCity = (city: City) => {
    setDestination(city);
    setDestSearch(city.name);
    setDestDropdownOpen(false);
  };

  // Swap source and destination
  const swapLocations = () => {
    const tempSource = source;
    const tempSourceSearch = sourceSearch;

    setSource(destination);
    setSourceSearch(destination ? destination.name : "");

    setDestination(tempSource);
    setDestSearch(tempSource ? tempSource.name : "");
  };

  // Format date as dd/mm/yyyy for display
  const formatDateForDisplay = (isoDate: string) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const selectVertical = (vertical: VerticalType) => {
    setActiveTab(vertical);
    setTripType(DEFAULT_TRIP_TYPE_BY_VERTICAL[vertical]);
  };

  // Same field-visibility rules as SearchSectionNew - single source of truth
  // lives in constants/tripTypes.ts so the two forms can never diverge again.
  const isPickUpVisible = isSourceFieldVisible(activeTab, tripType);
  const isDestinationVisible = isDestinationFieldVisible(activeTab, tripType);
  const showReturnDateTime = isReturnLegRequired(tripType);

  const visibleColumnCount =
    2 + (isPickUpVisible ? 1 : 0) + (isDestinationVisible ? 1 : 0);
  const gridColsClass =
    visibleColumnCount === 2
      ? "lg:grid-cols-2"
      : visibleColumnCount === 3
      ? "lg:grid-cols-3"
      : "lg:grid-cols-4";

  const formVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, delay: 0.15 } },
    exit: { opacity: 0, transition: { duration: 0.1 } },
  };

  return (
    <div
      className="bg-white rounded-lg shadow-lg overflow-visible"
      data-aos={animationEnabled ? "fade-up" : ""}
    >
      {/* Vertical tabs */}
      <div className="flex bg-black rounded-t-lg">
        {VERTICAL_OPTIONS.map((vertical) => (
          <button
            key={vertical.value}
            className={`px-6 py-4 font-medium text-sm flex-1 transition-colors duration-200 ${
              activeTab === vertical.value
                ? "bg-gray-300 text-black"
                : "text-white"
            }`}
            onClick={() => selectVertical(vertical.value)}
          >
            {vertical.label}
          </button>
        ))}
      </div>

      {/* Trip-type sub tabs, scoped to the active vertical */}
      <div className="flex bg-gray-800">
        {TRIP_TYPES_BY_VERTICAL[activeTab].map((option) => (
          <button
            key={option.value}
            className={`px-6 py-2 font-medium text-xs flex-1 transition-colors duration-200 ${
              tripType === option.value
                ? "bg-gray-300 text-black"
                : "text-gray-200"
            }`}
            onClick={() => setTripType(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Search form */}
      <div className="p-6 relative pb-14">
        <div className="flex flex-col gap-6">
          <motion.div layout transition={{ duration: 0.3, ease: "easeInOut" }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${activeTab}-${tripType}`}
                variants={formVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {/* Conditional rendering for non-hourly verticals */}
                {activeTab !== VERTICALS.HOURLY && (
                  <>
                    <div
                      className={`grid grid-cols-1 md:grid-cols-2 ${gridColsClass} gap-6`}
                    >
                      {/* Source location */}
                      {isPickUpVisible && (
                        <div className="relative" ref={sourceRef}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            From
                          </label>
                          <div
                            className="flex items-center border rounded-md p-3 bg-white cursor-pointer relative"
                            onClick={() =>
                              setSourceDropdownOpen(!sourceDropdownOpen)
                            }
                          >
                            <svg
                              className="w-5 h-5 text-gray-400 mr-2"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <input
                              type="text"
                              className="flex-grow outline-none text-gray-700 placeholder-gray-400"
                              placeholder="Enter city"
                              value={sourceSearch}
                              onChange={handleSourceSearchChange}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!sourceDropdownOpen) {
                                  setSourceDropdownOpen(true);
                                }
                              }}
                            />
                            {sourceSearch && (
                              <FontAwesomeIcon
                                icon={faTimes}
                                className="text-gray-400 cursor-pointer hover:text-gray-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSourceSearch("");
                                  setSource(null);
                                }}
                              />
                            )}
                          </div>
                          {sourceDropdownOpen && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                              {isLoadingSource ? (
                                <div className="p-3 text-center text-gray-500">
                                  Loading...
                                </div>
                              ) : sourceCities.length > 0 ? (
                                sourceCities.map((city) => (
                                  <div
                                    key={city.id}
                                    className="p-3 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => selectSourceCity(city)}
                                  >
                                    <div className="font-medium">{city.name}</div>
                                    <div className="text-sm text-gray-500">
                                      {city.state}, {city.region}
                                    </div>
                                  </div>
                                ))
                              ) : sourceSearch.length >= 2 ? (
                                <div className="p-3 text-gray-500">
                                  No cities found
                                </div>
                              ) : (
                                <div className="p-3 text-gray-500">
                                  Type at least 2 characters to search
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Destination location */}
                      {isDestinationVisible && (
                        <div className="relative" ref={destRef}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            To
                          </label>
                          <div className="flex items-center relative">
                            {/* Swap button - only relevant when both fields are visible */}
                            {isPickUpVisible && (
                              <button
                                className="absolute sm:-left-[25px] -left-[5px] sm:top-1/2 top-[35%] z-10 transform sm:-translate-y-1/2 -translate-y-full bg-blue-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-md hover:bg-blue-700"
                                onClick={swapLocations}
                                aria-label="Swap locations"
                              >
                                <svg
                                  className="w-4 h-4 sm:rotate-0 rotate-90"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M9 6L6 9L9 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M15 18L18 15L15 12"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M6 9H18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                  <path
                                    d="M18 15H6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            )}
                            <div
                              className="flex items-center border rounded-md p-3 bg-white cursor-pointer flex-grow pl-3"
                              onClick={() =>
                                setDestDropdownOpen(!destDropdownOpen)
                              }
                            >
                              <svg
                                className="w-5 h-5 text-gray-400 mr-2"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <input
                                type="text"
                                className="flex-grow outline-none text-gray-700 placeholder-gray-400"
                                placeholder="Enter city"
                                value={destSearch}
                                onChange={handleDestSearchChange}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!destDropdownOpen) {
                                    setDestDropdownOpen(true);
                                  }
                                }}
                              />
                              {destSearch && (
                                <FontAwesomeIcon
                                  icon={faTimes}
                                  className="text-gray-400 cursor-pointer hover:text-gray-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDestSearch("");
                                    setDestination(null);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          {destDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                              {isLoadingDest ? (
                                <div className="p-3 text-center text-gray-500">
                                  Loading...
                                </div>
                              ) : destinationCities.length > 0 ? (
                                destinationCities.map((city) => (
                                  <div
                                    key={city.id}
                                    className="p-3 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => selectDestinationCity(city)}
                                  >
                                    <div className="font-medium">{city.name}</div>
                                    <div className="text-sm text-gray-500">
                                      {city.state}, {city.region}
                                    </div>
                                  </div>
                                ))
                              ) : destSearch.length >= 2 ? (
                                <div className="p-3 text-gray-500">
                                  No cities found
                                </div>
                              ) : (
                                <div className="p-3 text-gray-500">
                                  Type at least 2 characters to search
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Departure date - always applies */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Departure Date
                        </label>
                        <div className="relative">
                          <div
                            className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                            onClick={() => {
                              if (departureDateRef.current) {
                                departureDateRef.current.showPicker();
                              }
                            }}
                          >
                            <svg
                              className="w-5 h-5 text-gray-400 mr-2"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <input
                              type="text"
                              className="w-full outline-none text-gray-700 placeholder-gray-400"
                              placeholder="dd/mm/yyyy"
                              value={
                                departureDate
                                  ? formatDateForDisplay(departureDate)
                                  : ""
                              }
                              readOnly
                            />
                            <input
                              ref={departureDateRef}
                              type="date"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              value={departureDate}
                              onChange={(e) => setDepartureDate(e.target.value)}
                              min={new Date().toISOString().split("T")[0]}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Pickup time - always applies */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pickup Time
                        </label>
                        <div className="relative">
                          <div
                            className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                            onClick={() => {
                              if (pickupTimeRef.current) {
                                pickupTimeRef.current.showPicker();
                              }
                            }}
                          >
                            <svg
                              className="w-5 h-5 text-gray-400 mr-2"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <circle
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <path
                                d="M12 6V12L16 14"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                            </svg>
                            <input
                              type="text"
                              className="w-full outline-none text-gray-700 placeholder-gray-400"
                              placeholder="--:-- --"
                              value={
                                pickupTime
                                  ? new Date(
                                      `2000-01-01T${pickupTime}`
                                    ).toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : ""
                              }
                              readOnly
                            />
                            <input
                              ref={pickupTimeRef}
                              type="time"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              value={pickupTime}
                              onChange={(e) => setPickupTime(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Second row - round trip only */}
                    {showReturnDateTime && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        {/* Return date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Return Date
                          </label>
                          <div className="relative">
                            <div
                              className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                              onClick={() => {
                                if (returnDateRef.current) {
                                  returnDateRef.current.showPicker();
                                }
                              }}
                            >
                              <svg
                                className="w-5 h-5 text-gray-400 mr-2"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              <input
                                type="text"
                                className="w-full outline-none text-gray-700 placeholder-gray-400"
                                placeholder="dd/mm/yyyy"
                                value={
                                  returnDate
                                    ? formatDateForDisplay(returnDate)
                                    : ""
                                }
                                readOnly
                              />
                              <input
                                ref={returnDateRef}
                                type="date"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                value={returnDate}
                                onChange={(e) => setReturnDate(e.target.value)}
                                min={
                                  departureDate ||
                                  new Date().toISOString().split("T")[0]
                                }
                              />
                            </div>
                          </div>
                        </div>

                        {/* Drop time */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Drop Time
                          </label>
                          <div className="relative">
                            <div
                              className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                              onClick={() => {
                                if (dropTimeRef.current) {
                                  dropTimeRef.current.showPicker();
                                }
                              }}
                            >
                              <svg
                                className="w-5 h-5 text-gray-400 mr-2"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                />
                                <path
                                  d="M12 6V12L16 14"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                              <input
                                type="text"
                                className="w-full outline-none text-gray-700 placeholder-gray-400"
                                placeholder="--:-- --"
                                value={
                                  dropTime
                                    ? new Date(
                                        `2000-01-01T${dropTime}`
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : ""
                                }
                                readOnly
                              />
                              <input
                                ref={dropTimeRef}
                                type="time"
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                value={dropTime}
                                onChange={(e) => setDropTime(e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Conditional rendering for hourly vertical */}
                {activeTab === VERTICALS.HOURLY && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Pickup Location */}
                    <div className="relative" ref={sourceRef}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Location
                      </label>
                      <div
                        className="flex items-center border rounded-md p-3 bg-white cursor-pointer relative"
                        onClick={() =>
                          setSourceDropdownOpen(!sourceDropdownOpen)
                        }
                      >
                        <svg
                          className="w-5 h-5 text-gray-400 mr-2"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className="flex-grow outline-none text-gray-700 placeholder-gray-400"
                          placeholder="Enter city"
                          value={sourceSearch}
                          onChange={handleSourceSearchChange}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!sourceDropdownOpen) {
                              setSourceDropdownOpen(true);
                            }
                          }}
                        />
                        {sourceSearch && (
                          <FontAwesomeIcon
                            icon={faTimes}
                            className="text-gray-400 cursor-pointer hover:text-gray-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSourceSearch("");
                              setSource(null);
                            }}
                          />
                        )}
                      </div>
                      {sourceDropdownOpen && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {isLoadingSource ? (
                            <div className="p-3 text-center text-gray-500">
                              Loading...
                            </div>
                          ) : sourceCities.length > 0 ? (
                            sourceCities.map((city) => (
                              <div
                                key={city.id}
                                className="p-3 hover:bg-gray-100 cursor-pointer"
                                onClick={() => selectSourceCity(city)}
                              >
                                <div className="font-medium">{city.name}</div>
                                <div className="text-sm text-gray-500">
                                  {city.state}, {city.region}
                                </div>
                              </div>
                            ))
                          ) : sourceSearch.length >= 2 ? (
                            <div className="p-3 text-gray-500">
                              No cities found
                            </div>
                          ) : (
                            <div className="p-3 text-gray-500">
                              Type at least 2 characters to search
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Departure Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Departure Date
                      </label>
                      <div className="relative">
                        <div
                          className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                          onClick={() => {
                            if (departureDateRef.current) {
                              departureDateRef.current.showPicker();
                            }
                          }}
                        >
                          <svg
                            className="w-5 h-5 text-gray-400 mr-2"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <input
                            type="text"
                            className="w-full outline-none text-gray-700 placeholder-gray-400"
                            placeholder="dd/mm/yyyy"
                            value={
                              departureDate
                                ? formatDateForDisplay(departureDate)
                                : ""
                            }
                            readOnly
                          />
                          <input
                            ref={departureDateRef}
                            type="date"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            value={departureDate}
                            onChange={(e) => setDepartureDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Pickup Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Time
                      </label>
                      <div className="relative">
                        <div
                          className="flex items-center border rounded-md p-3 bg-white cursor-pointer"
                          onClick={() => {
                            if (pickupTimeRef.current) {
                              pickupTimeRef.current.showPicker();
                            }
                          }}
                        >
                          <svg
                            className="w-5 h-5 text-gray-400 mr-2"
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="2"
                            />
                            <path
                              d="M12 6V12L16 14"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                          </svg>
                          <input
                            type="text"
                            className="w-full outline-none text-gray-700 placeholder-gray-400"
                            placeholder="--:-- --"
                            value={
                              pickupTime
                                ? new Date(
                                    `2000-01-01T${pickupTime}`
                                  ).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""
                            }
                            readOnly
                          />
                          <input
                            ref={pickupTimeRef}
                            type="time"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            value={pickupTime}
                            onChange={(e) => setPickupTime(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Distance/route preview - once both locations are picked */}
                {isDestinationVisible && source && destination && (
                  <div className="mt-6">
                    <LocationDistanceMap
                      origin={{
                        lat: source.latitude,
                        lng: source.longitude,
                        name: source.name,
                      }}
                      destination={{
                        lat: destination.latitude,
                        lng: destination.longitude,
                        name: destination.name,
                      }}
                      onDistanceComputed={onDistanceComputed}
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Search and Clear buttons */}
        <div className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 flex items-center gap-4">
          <button
            className={`text-white font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap ${
              !isSearchEnabled
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            onClick={handleSearch}
            disabled={!isSearchEnabled}
          >
            Search Cabs
          </button>
          {showClearButton && (
            <button
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-12 rounded-md shadow-md transition-colors duration-200 whitespace-nowrap"
              onClick={handleClearFilter}
            >
              Clear Filter
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchSection;
